import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, List, ListItem, ListItemText, Chip } from '@mui/material';
import { CheckCircle, Error, Warning } from '@mui/icons-material';

interface PWAStatus {
  hasServiceWorker: boolean;
  hasManifest: boolean;
  isHTTPS: boolean;
  isInstalled: boolean;
  serviceWorkerState?: string;
  manifestData?: any;
  installPromptAvailable: boolean;
}

export const PWADebug: React.FC = () => {
  const [pwaStatus, setPwaStatus] = useState<PWAStatus>({
    hasServiceWorker: false,
    hasManifest: false,
    isHTTPS: false,
    isInstalled: false,
    installPromptAvailable: false
  });

  useEffect(() => {
    const checkBasicStatus = () => {
      const hasServiceWorker = 'serviceWorker' in navigator;
      const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
      const hasManifest = !!manifestLink;
      const isHTTPS = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
      const isInstalled = window.matchMedia('(display-mode: standalone)').matches;

      setPwaStatus({
        hasServiceWorker,
        hasManifest,
        isHTTPS,
        isInstalled,
        serviceWorkerState: hasServiceWorker ? 'Checking...' : 'Not supported',
        manifestData: null,
        installPromptAvailable: false
      });
    };

    checkBasicStatus();
  }, []);

  const getStatusIcon = (condition: boolean) => {
    return condition ? (
      <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
    ) : (
      <Error sx={{ color: 'error.main', mr: 1 }} />
    );
  };

  const refreshCheck = () => {
    // Instead of full reload, just re-run the check
    const checkPWAStatus = async () => {
      // Check Service Worker
      const hasServiceWorker = 'serviceWorker' in navigator;
      let serviceWorkerState = 'Not supported';

      if (hasServiceWorker) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          if (registrations.length > 0) {
            serviceWorkerState = registrations[0].active?.state || 'Registering...';
          } else {
            serviceWorkerState = 'Not registered';
          }
        } catch (error) {
          serviceWorkerState = 'Error checking registration';
        }
      }

      // Check Manifest
      const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
      const hasManifest = !!manifestLink;
      let manifestData = null;

      if (hasManifest) {
        try {
          const response = await fetch(manifestLink.href);
          manifestData = await response.json();
        } catch (error) {
          console.error('Error fetching manifest:', error);
        }
      }

      // Check HTTPS
      const isHTTPS = window.location.protocol === 'https:' || window.location.hostname === 'localhost';

      // Check if installed
      const isInstalled = window.matchMedia('(display-mode: standalone)').matches ||
                          (window.navigator as any).standalone === true;

      setPwaStatus({
        hasServiceWorker,
        hasManifest,
        isHTTPS,
        isInstalled,
        serviceWorkerState,
        manifestData,
        installPromptAvailable: false // Reset this
      });
    };

    checkPWAStatus();
  };

  const testInstallPrompt = () => {
    console.log('Testing install prompt...');
    // This will only work if the beforeinstallprompt event has been fired
    const event = new Event('beforeinstallprompt');
    window.dispatchEvent(event);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          PWA Debug Dashboard
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          PWA Installation Requirements
        </Typography>

        <List>
          <ListItem>
            {getStatusIcon(pwaStatus.isHTTPS)}
            <ListItemText
              primary="HTTPS Connection"
              secondary={`Current: ${window.location.protocol} (localhost counts as secure)`}
            />
            <Chip
              label={pwaStatus.isHTTPS ? "Pass" : "Fail"}
              color={pwaStatus.isHTTPS ? "success" : "error"}
            />
          </ListItem>

          <ListItem>
            {getStatusIcon(pwaStatus.hasServiceWorker && pwaStatus.serviceWorkerState === 'activated')}
            <ListItemText
              primary="Service Worker"
              secondary={`State: ${pwaStatus.serviceWorkerState}`}
            />
            <Chip
              label={pwaStatus.hasServiceWorker ? "Registered" : "Missing"}
              color={pwaStatus.hasServiceWorker ? "success" : "error"}
            />
          </ListItem>

          <ListItem>
            {getStatusIcon(pwaStatus.hasManifest)}
            <ListItemText
              primary="Web App Manifest"
              secondary={pwaStatus.manifestData ? `Found: ${pwaStatus.manifestData.name}` : "Manifest not found or invalid"}
            />
            <Chip
              label={pwaStatus.hasManifest ? "Valid" : "Missing"}
              color={pwaStatus.hasManifest ? "success" : "error"}
            />
          </ListItem>

          <ListItem>
            {getStatusIcon(!pwaStatus.isInstalled)}
            <ListItemText
              primary="Not Already Installed"
              secondary={pwaStatus.isInstalled ? "App is already installed" : "App not installed - good for testing"}
            />
            <Chip
              label={pwaStatus.isInstalled ? "Installed" : "Not Installed"}
              color={!pwaStatus.isInstalled ? "success" : "warning"}
            />
          </ListItem>
        </List>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Install Prompt Status
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {pwaStatus.installPromptAvailable ? (
            <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
          ) : (
            <Warning sx={{ color: 'warning.main', mr: 1 }} />
          )}
          <Typography>
            Install prompt: {pwaStatus.installPromptAvailable ? 'Available' : 'Not available yet'}
          </Typography>
        </Box>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Note: The install prompt may take a few seconds to become available after all requirements are met.
          You may also need to interact with the page (click something) for the prompt to appear.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={refreshCheck}>
            Refresh Check
          </Button>
          <Button variant="outlined" onClick={testInstallPrompt}>
            Test Install Event
          </Button>
        </Box>

        {pwaStatus.manifestData && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Manifest Data
            </Typography>
            <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
              <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                {JSON.stringify(pwaStatus.manifestData, null, 2)}
              </pre>
            </Paper>
          </Box>
        )}
      </Paper>
    </Box>
  );
};